import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import type { LocationId } from "@redotech/redo-inventory-id";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import type { ProductId } from "@redotech/redo-product-id";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Skeleton } from "@redotech/redo-web/components/ui/skeleton";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Fragment, memo, ReactNode, useMemo, useState } from "react";
import { LotBadge } from "../wms/shared/lot-badge";

export interface InventoryModalRow {
  key: string;
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  locationName?: string | null;
  onHand: number | null;
  committed: number | null;
  available: number | null;
  /** Set on rows at WMS source-of-truth locations; lets the row expand into its bins. */
  bins?: { productId: ProductId; locationId: LocationId } | null;
}

interface InventoryModalProps {
  rows: InventoryModalRow[];
  isOpen: boolean;
  loading?: boolean;
  onClose: () => void;
}

const NUMBER_COLUMN_WIDTH = 108;

const NUMBER_COLUMNS: {
  key: string;
  label: string;
  value: (row: InventoryModalRow) => number | null;
}[] = [
  { key: "onHand", label: "On hand", value: (row) => row.onHand },
  { key: "committed", label: "Committed", value: (row) => row.committed },
  { key: "available", label: "Available", value: (row) => row.available },
];

type RenderItem =
  | { type: "location"; locationName: string }
  | { type: "product"; row: InventoryModalRow };

const NumberCell = memo(function NumberCell({
  children,
}: {
  children: ReactNode;
}) {
  return (
    // design-allow: fixed width keeps each number under its column header
    <Flex justify="center" style={{ width: NUMBER_COLUMN_WIDTH }}>
      {children}
    </Flex>
  );
});

/** A product's bins at one location; mirrors the product page's Inventory tab. */
const InventoryModalBinRows = memo(function InventoryModalBinRows({
  productId,
  locationId,
}: {
  productId: ProductId;
  locationId: LocationId;
}) {
  const inventoryRpcClient = useRequiredContext(RedoInventoryRpcClientContext);
  const binsLoad = useLoad(
    async (signal) => {
      const { bins } = await inventoryRpcClient.listProductBinsAtLocation(
        { productIds: [productId], locationId, includeLots: true },
        { signal },
      );
      return bins;
    },
    [inventoryRpcClient, productId, locationId],
  );

  if (binsLoad.pending) {
    return (
      <Flex bgColor="secondary" pl="6xl" pr="xl" py="sm" w="full">
        <Skeleton height={12} width={120} />
      </Flex>
    );
  }
  const bins = [...(binsLoad.value ?? [])].sort(
    (a, b) => b.quantity - a.quantity || a.binName.localeCompare(b.binName),
  );
  if (binsLoad.error || bins.length === 0) {
    return (
      <Flex bgColor="secondary" pl="6xl" pr="xl" py="sm" w="full">
        <Text fontSize="xxs" textColor="secondary">
          {binsLoad.error ? "Couldn't load bins" : "No bins hold this product"}
        </Text>
      </Flex>
    );
  }
  return (
    <>
      {bins.map((bin) => (
        <Flex
          align="center"
          bgColor="secondary"
          justify="space-between"
          key={`${bin.storageLocationId}:${bin.inventoryItemId ?? ""}`}
          pl="6xl"
          pr="xl"
          py="sm"
          w="full"
        >
          <Flex align="center" gap="xs" wrap="wrap">
            <Text fontSize="xs">{bin.binName}</Text>
            {bin.lotNumber ? (
              <LotBadge
                expirationDate={bin.expirationDate}
                lotNumber={bin.lotNumber}
              />
            ) : null}
            {bin.binType && bin.binType !== "bin" ? (
              <RedoBadge color="gray" size="xs" text={bin.binType} />
            ) : null}
            {bin.isSellable === false ? (
              <RedoBadge color="warning" size="xs" text="Unsellable" />
            ) : null}
          </Flex>
          <Flex align="center">
            <NumberCell>
              <Text fontSize="xs" fontWeight="thin" textColor="secondary">
                {bin.quantity}
              </Text>
            </NumberCell>
            <NumberCell>
              <Text fontSize="xs" textColor="secondary">
                —
              </Text>
            </NumberCell>
            <NumberCell>
              <Text fontSize="xs" textColor="secondary">
                —
              </Text>
            </NumberCell>
          </Flex>
        </Flex>
      ))}
    </>
  );
});

export const InventoryModal = memo(function InventoryModal({
  rows,
  isOpen,
  loading = false,
  onClose,
}: InventoryModalProps) {
  // Rows with bins start expanded; track the ones the user has closed.
  const [collapsedKeys, setCollapsedKeys] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const toggleExpanded = (key: string) =>
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const items = useMemo<RenderItem[]>(() => {
    const groups: { locationName: string | null; rows: InventoryModalRow[] }[] =
      [];
    const groupIndex = new Map<string, number>();
    for (const row of rows) {
      const locationName = row.locationName ?? null;
      const groupKey = locationName ?? "\u0000";
      let index = groupIndex.get(groupKey);
      if (index === undefined) {
        index = groups.length;
        groupIndex.set(groupKey, index);
        groups.push({ locationName, rows: [] });
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      groups[index]!.rows.push(row);
    }

    const showLocationHeaders = groups.some(
      (group) => group.locationName != null,
    );

    const result: RenderItem[] = [];
    for (const group of groups) {
      if (showLocationHeaders) {
        result.push({
          type: "location",
          locationName: group.locationName ?? "Not tracked",
        });
      }
      for (const row of group.rows) {
        result.push({ type: "product", row });
      }
    }
    return result;
  }, [rows]);

  return (
    <RedoBaseModal isOpen={isOpen} onModalCloseRequested={onClose}>
      <Flex dir="column" gap="none" w="full">
        <RedoModalHeader
          cancelClicked={onClose}
          hideCloseButton
          title="Current inventory"
        />
        <Flex dir="column" gap="none" w="full">
          <Flex
            bgColor="secondary"
            borderBottomWidth="1px"
            borderColor="primary"
            borderStyle="solid"
            borderTopWidth="1px"
            justify="space-between"
            pl="2xl"
            pr="xl"
            py="lg"
            w="full"
          >
            <Text fontSize="xxs" textColor="secondary">
              Products
            </Text>
            <Flex>
              {NUMBER_COLUMNS.map((column) => (
                <Flex
                  justify="center"
                  key={column.key}
                  style={{ width: NUMBER_COLUMN_WIDTH }}
                >
                  <Text fontSize="xxs" textColor="secondary">
                    {column.label}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
          <Flex
            dir="column"
            gap="none"
            style={{ maxHeight: "50vh", overflowY: "auto" }}
            w="full"
          >
            {items.map((item, index) => {
              const notLast = index !== items.length - 1;
              if (item.type === "location") {
                return (
                  <Flex
                    align="center"
                    bgColor="secondary"
                    borderBottomWidth={notLast ? "1px" : "none"}
                    borderColor="secondary"
                    borderStyle="solid"
                    key={`location-${item.locationName}`}
                    pl="2xl"
                    pr="xl"
                    py="xs"
                    w="full"
                  >
                    <Text fontSize="xxs" textColor="secondary">
                      {item.locationName}
                    </Text>
                  </Flex>
                );
              }
              const { row } = item;
              const isExpanded = !!row.bins && !collapsedKeys.has(row.key);
              return (
                <Fragment key={row.key}>
                  <Flex
                    align="center"
                    borderBottomWidth={notLast || isExpanded ? "1px" : "none"}
                    borderColor="secondary"
                    borderStyle="solid"
                    justify="space-between"
                    pl={row.bins ? "md" : "2xl"}
                    pr="xl"
                    py="md"
                    w="full"
                  >
                    <Flex align="center" gap="md">
                      {row.bins ? (
                        <RedoButton
                          aria-label={isExpanded ? "Hide bins" : "Show bins"}
                          data-dd-action-name="inventory_modal:toggle_bins_click"
                          hierarchy="tertiary"
                          IconLeading={
                            isExpanded ? ChevronDownIcon : ChevronRightIcon
                          }
                          onClick={() => toggleExpanded(row.key)}
                          size="xs"
                        />
                      ) : null}
                      {row.imageUrl ? (
                        <img
                          alt={row.title}
                          src={row.imageUrl}
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: "var(--border-radius-full)",
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                      <Flex dir="column" gap="xs" justify="center">
                        <Text fontSize="xs" fontWeight="medium">
                          {row.title}
                        </Text>
                        {row.subtitle ? (
                          <Text fontSize="xxs" textColor="secondary">
                            {row.subtitle}
                          </Text>
                        ) : null}
                      </Flex>
                    </Flex>
                    <Flex align="center">
                      {NUMBER_COLUMNS.map((column) => (
                        <Flex
                          justify="center"
                          key={column.key}
                          style={{ width: NUMBER_COLUMN_WIDTH }}
                        >
                          {loading ? (
                            <Skeleton height={12} width={28} />
                          ) : (
                            <Text
                              fontSize="xs"
                              fontWeight="thin"
                              textColor="secondary"
                            >
                              {column.value(row) ?? "N/A"}
                            </Text>
                          )}
                        </Flex>
                      ))}
                    </Flex>
                  </Flex>
                  {isExpanded && row.bins ? (
                    <InventoryModalBinRows
                      locationId={row.bins.locationId}
                      productId={row.bins.productId}
                    />
                  ) : null}
                </Fragment>
              );
            })}
          </Flex>
        </Flex>
        <Flex
          borderColor="secondary"
          borderStyle="solid"
          borderTopWidth="1px"
          dir="row"
          gap="md"
          justify="flex-end"
          px="2xl"
          py="lg"
          w="full"
        >
          <RedoButton
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Close"
            theme="normal"
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
